import { trackCustomEvent } from "analytics";
import { useFocusEffect } from "expo-router";
/* -----------------Globals--------------- */
import { useCallback, useEffect, useState } from "react";
import { getShouldShowReviewPrompt } from "../helpers";
import { useReviewStore } from "./useReviewStore";

import { currentAppVersion } from "@helpers/expo-extras";
/* -----------------Constants--------------- */
import { feedbackEvents } from "../constants";

/* -----------------Third party libraries--------------- */
import * as StoreReview from "expo-store-review";

export const useReview = () => {
	const [showReviewPrompt, setShouldShowReviewPrompt] = useState(false);
	const [hasFocus, setHasFocus] = useState(false);

	const [currentStep, setCurrentStep] = useState<"prompt" | "feedback">(
		"prompt",
	);
	const isFeedbackStep = currentStep === "feedback";

	const resetTrackedAppReviewCritieria = useReviewStore(
		(state) => state.resetTrackedAppReviewCritieria,
	);
	const isRoutedFromSession = useReviewStore(
		(state) => state.isRoutedFromSession,
	);
	const updateIsRoutedFromSession = useReviewStore(
		(state) => state.updateIsRoutedFromSession,
	);
	const updateLastAppVersion = useReviewStore(
		(state) => state.updateLastAppVersion,
	);

	//TODO: Make this dynamic
	const isPremiumUser = false;

	useFocusEffect(
		useCallback(() => {
			setHasFocus(true);
			return () => {
				setHasFocus(false);
				updateIsRoutedFromSession(false);
			};
		}, [updateIsRoutedFromSession]),
	);

	useFocusEffect(
		useCallback(() => {
			if (isPremiumUser === undefined) {
				return;
			}
			const shouldShowReviewPrompt = getShouldShowReviewPrompt(isPremiumUser);
			setShouldShowReviewPrompt(shouldShowReviewPrompt);
		}, []),
	);

	const showReviewDialog = showReviewPrompt && hasFocus && isRoutedFromSession;

	useEffect(() => {
		if (showReviewDialog) {
			const eventName = isFeedbackStep
				? feedbackEvents.feedbackDialogOpened
				: feedbackEvents.reviewDialogOpened;
			trackCustomEvent({
				eventName,
			});
		}
	}, [isFeedbackStep, showReviewDialog]);

	useEffect(() => {
		if (showReviewDialog) {
			updateLastAppVersion(currentAppVersion!);
		}
	}, [showReviewDialog, updateLastAppVersion]);

	const handleDismiss = () => {
		const eventName = isFeedbackStep
			? feedbackEvents.feedbackDialogClosed
			: feedbackEvents.reviewDialogClosed;
		trackCustomEvent({
			eventName,
		});
		setShouldShowReviewPrompt(false);
		resetTrackedAppReviewCritieria();
	};

	const handleReviewButtonsClick = (type: "positive" | "negative") => {
		if (type === "positive") {
			trackCustomEvent({
				eventName: feedbackEvents.reviewPositiveClicked,
			});
			StoreReview.requestReview().then(handleDismiss);
		} else {
			resetTrackedAppReviewCritieria();
			trackCustomEvent({
				eventName: feedbackEvents.reviewNegativeClicked,
			});
			setCurrentStep("feedback");
		}
	};

	const handleDismissFeedback = () => {
		handleDismiss();
	};

	return {
		showReviewPrompt,
		hasFocus,
		isRoutedFromSession,
		handleReviewButtonsClick,
		handleDismissFeedback,
		currentStep,
	};
};
